import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatDateOnly, formatDateInUserTZ } from '../../utils/timezone';
import { Money } from '../../ui';
import { resolveDiscount, toCents, toDollars, computeLineItems } from '../../utils/discountMath';
import './PublicEstimate.css';

// PublicEstimate — v2.8.1.
//
// Same editorial layout as PublicInvoice with three differences:
//   1. "Estimate" mark instead of "Invoice", EST-numbering.
//   2. Accept (brand primary) + Decline (ghost) buttons, no Pay flow.
//   3. Supersession banner if a child revision exists.
//
// Locked to light mode (color-scheme: light) — same reasoning as
// PublicInvoice: clients print and forward these.

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── What a client is told when accept/decline fails ─────────────────────────
//
// `handleAccept`/`handleDecline` used to call `res.json()` BEFORE checking
// `res.ok`, and their `catch` put `e.message` straight into `actionError`. An
// nginx 502 (PM2 restarting `fcp-tracker`) or a 500 with an empty body makes
// `res.json()` reject with a SyntaxError that quotes the raw body, so the
// action bar of this page — one of only two screens a paying CLIENT ever sees
// — rendered:
//
//     Unexpected token '<', "<html>..." is not valid JSON
//
// A homeowner cannot act on that, cannot tell whether their answer was
// recorded, and is reading it while deciding whether to spend thousands of
// dollars with the company. So: check `res.ok` first, and never put a thrown
// message on screen. Both sentences state that the estimate has NOT changed,
// because "did I just half-accept this?" is the actual worry.

const contactSuffix = (phone) => (phone ? `, or call us at ${phone}.` : ', or call us.');

/** The server answered, but with something unusable. */
const serverFailedMessage = (phone) =>
  'Something went wrong on our end. Your estimate has not changed — please try'
  + ` again${contactSuffix(phone)}`;

/** No answer at all — a dropped connection, which is a different fix for them. */
const cannotReachMessage = (phone) =>
  'We could not reach the server. Your estimate has not changed — please check'
  + ` your connection and try again${contactSuffix(phone)}`;

// ── What a client is told when the DOCUMENT itself will not load ────────────
//
// The loader threw one `new Error('Not found')` for every failure — a 404, an
// nginx 502 while PM2 restarts `fcp-tracker`, a 500 from a bad query, a
// dropped connection — and rendered "Estimate not found or link has expired."
// for all of them. For anything but the 404 that is a FALSE statement with a
// business consequence: a client who believes the offer lapsed does not retry
// and may not ring, because the reasonable reading is that the company
// withdrew it. The action bar below already draws this distinction; the
// loader, which every single visitor goes through, did not.
//
// Neither sentence can offer a phone number: the company's details arrive in
// the payload of the request that just failed.
const LINK_GONE = 'Estimate not found or link has expired.';
const CANNOT_LOAD = 'We could not load this estimate just now — this is a problem on our '
  + 'end, not with your link. Please try again in a moment.';

/**
 * Turn a non-ok response into a sentence, preferring the server's own words
 * when it actually sent JSON. Parser errors are swallowed here rather than
 * thrown, so there is no path by which one reaches the DOM.
 */
const refusalMessage = async (res, phone) => {
  try {
    const json = await res.json();
    if (json && json.error) return json.error;
  } catch (_e) {
    // Body was not JSON at all: an HTML error page, or nothing.
  }
  return serverFailedMessage(phone);
};

function PublicEstimate() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionState, setActionState] = useState({
    decliningOpen: false,
    declineReason: '',
    busy: false,
    actionError: '',
    justAccepted: false,
    justDeclined: false,
  });

  useEffect(() => {
    fetch(`/api/estimates/public/${token}`)
      // 404 is the only answer that means the link itself is finished. Every
      // other failure — including a body that will not parse — is ours.
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'gone' : 'down'); return r.json(); })
      .then(d => setData(d))
      .catch(e => setError(e && e.message === 'gone' ? LINK_GONE : CANNOT_LOAD))
      .finally(() => setLoading(false));
  }, [token]);

  const refetch = async () => {
    try {
      const r = await fetch(`/api/estimates/public/${token}`);
      if (r.ok) setData(await r.json());
    } catch (_e) {}
  };

  const handleAccept = async () => {
    setActionState(s => ({ ...s, busy: true, actionError: '' }));
    const phone = data?.company?.phone;
    try {
      const res = await fetch(`/api/estimates/public/${token}/accept`, { method: 'POST' });
      if (!res.ok) {
        const message = await refusalMessage(res, phone);
        setActionState(s => ({ ...s, busy: false, actionError: message }));
        return;
      }
      // A 200 means the server recorded it; the body is not needed, and
      // parsing it would reintroduce a failure mode on the success path.
      setActionState(s => ({ ...s, busy: false, justAccepted: true }));
      refetch();
    } catch (_e) {
      setActionState(s => ({ ...s, busy: false, actionError: cannotReachMessage(phone) }));
    }
  };

  const handleDecline = async () => {
    setActionState(s => ({ ...s, busy: true, actionError: '' }));
    const phone = data?.company?.phone;
    try {
      const res = await fetch(`/api/estimates/public/${token}/decline`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: actionState.declineReason || null }),
      });
      if (!res.ok) {
        // Leave the form open with the reason intact, so a retry does not cost
        // the client their typing.
        const message = await refusalMessage(res, phone);
        setActionState(s => ({ ...s, busy: false, actionError: message }));
        return;
      }
      setActionState(s => ({ ...s, busy: false, decliningOpen: false, justDeclined: true }));
      refetch();
    } catch (_e) {
      setActionState(s => ({ ...s, busy: false, actionError: cannotReachMessage(phone) }));
    }
  };

  if (loading) return (
    <div className="pest-shell pest-shell--center">
      <div className="pest-loading">Loading estimate…</div>
    </div>
  );

  if (error) return (
    <div className="pest-shell pest-shell--center">
      <div className="pest-notfound">
        <h2>Estimate not available</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  const { estimate, items, photos, company, superseded_by } = data;
  const status = estimate.estimate_status;
  const isAccepted = status === 'accepted' || actionState.justAccepted;
  const isDeclined = status === 'declined' || actionState.justDeclined;
  const isExpired  = status === 'expired';
  const canRespond = status === 'sent'
    && !isAccepted && !isDeclined && !isExpired
    && !superseded_by;

  return (
    <div className="pest-shell">
      <article className="pest-doc">

        {/* ── Supersession banner ─────────────────────────────────── */}
        {superseded_by && (
          <div className="pest-supersede" role="alert">
            <strong>This estimate has been replaced.</strong> A newer
            version ({superseded_by.invoice_number}) was issued. Please
            ask your contact at {company?.name} for the latest link.
          </div>
        )}

        {/* ── Top mark + estimate number ───────────────────────────── */}
        <header className="pest-head">
          <div className="pest-head__brand">
            {company?.logo && (
              <img src={company.logo} alt="" className="pest-head__logo" />
            )}
            <div className="pest-head__company">
              <h1 className="pest-head__company-name">{company?.name}</h1>
              {company?.address && <div className="pest-head__company-meta">{company.address}</div>}
              {company?.phone   && <div className="pest-head__company-meta">{company.phone}</div>}
              {company?.email   && <div className="pest-head__company-meta">{company.email}</div>}
            </div>
          </div>

          <div className="pest-head__doc">
            <div className="pest-head__label">Estimate</div>
            <div className="pest-head__number fcp-tabular">{estimate.invoice_number}</div>
            <div className="pest-head__dates">
              <div>
                <span className="pest-head__date-label">Issued</span>
                <span className="pest-head__date-value fcp-tabular">
                  {/* Estimates share the `invoices` table, so this is the same
                      bare SQLite CURRENT_TIMESTAMP as PublicInvoice's issued
                      date (server/database.js:314) and the public route sends
                      the whole row untouched (`publicView`, server/routes/
                      estimates/list.js).
                      `new Date()` read it as LOCAL, so an estimate written up
                      at 9pm Eastern was dated the NEXT day on the quote the
                      client is deciding on — and that date sits directly above
                      an "Expires" line the client is meant to read it against.
                      Now goes through the same helper as that Expires line. */}
                  {formatDateOnly(estimate.created_at)}
                </span>
              </div>
              {estimate.expires_at && (
                <div>
                  <span className="pest-head__date-label">Expires</span>
                  <span className="pest-head__date-value fcp-tabular">
                    {formatDateOnly(estimate.expires_at)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Status banner ─────────────────────────────────────────── */}
        {isAccepted && (
          <div className="pest-status pest-status--accepted">
            <span className="pest-status__icon"><CheckIcon /></span>
            <span className="pest-status__label">Accepted</span>
            {estimate.accepted_at && (
              <span className="pest-status__meta fcp-tabular">
                {/* `accepted_at` is set by CURRENT_TIMESTAMP the moment the
                    client taps Accept on this very page
                    (`publicAccept`, server/routes/estimates/workflow.js), so
                    the wrong-day case is
                    reachable by the client's own action: accept at 8:30pm
                    Eastern, and the banner immediately claims it was accepted
                    tomorrow. Left on `formatDateInUserTZ` rather than
                    `formatDateOnly` only to keep the long month name. */}
                · {formatDateInUserTZ(estimate.accepted_at, { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
        )}
        {isDeclined && (
          <div className="pest-status pest-status--declined">
            <span className="pest-status__label">Declined</span>
          </div>
        )}
        {isExpired && (
          <div className="pest-status pest-status--expired">
            <span className="pest-status__label">Expired</span>
          </div>
        )}

        {/* ── Prepared for ──────────────────────────────────────────── */}
        <section className="pest-billto">
          <div className="pest-billto__label">Prepared for</div>
          <div className="pest-billto__name">{estimate.client_name}</div>
          {estimate.client_address && <div className="pest-billto__line">{estimate.client_address}</div>}
          {estimate.client_email && <div className="pest-billto__line">{estimate.client_email}</div>}
        </section>

        {/* ── Items table ───────────────────────────────────────────── */}
        <section className="pest-items">
          <table className="pest-items__table responsive-cards">
            <thead>
              <tr>
                <th className="pest-items__th">Description</th>
                <th className="pest-items__th pest-items__th--num">Qty</th>
                <th className="pest-items__th pest-items__th--num">Unit price</th>
                <th className="pest-items__th pest-items__th--num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                // SETTLED TO CENTS, the same way the stored subtotal was.
                // `quantity * unit_price` unrounded, handed to <Money>, formats
                // through Intl — and Intl rounds the decimal STRING while the
                // subtotal was built with Math.round(x * 100). They disagree
                // wherever the product lands near a half-cent:
                //   1.5 x $15.53 = 23.295
                //     Intl of the raw double        -> "$23.29"  printed here
                //     Math.round(23.295*100) = 2330 -> "$23.30"  in the total
                // Measured on this branch: 255 of 18,558 line shapes. A client
                // adding up this column got a different number from the
                // Subtotal underneath it. toCents/toDollars is the one
                // conversion discountMath and the Stripe path already use.
                const gross = toDollars(toCents((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)));
                const disc = resolveDiscount(gross, item.discount_amount, item.discount_type);
                const net = Math.max(0, gross - disc);
                const liPhotos = Array.isArray(item.photos) ? item.photos : [];
                return (
                <tr key={idx}>
                  <td className="pest-items__td pest-items__desc" data-label="Description">
                    {item.description}
                    {item.notes && <div className="pest-items__note">{item.notes}</div>}
                    {disc > 0 && (
                      <div className="pest-items__linedisc">
                        Discount: −<Money amount={disc} />
                        {item.discount_type === 'percent' && parseFloat(item.discount_amount) > 0 ? ` (${parseFloat(item.discount_amount)}%)` : ''}
                      </div>
                    )}
                    {liPhotos.length > 0 && (
                      <div className="pest-items__photos">
                        {liPhotos.map((p, i) => (
                          <img key={i} className="pest-items__photo"
                            src={p.file_path ? (p.file_path.startsWith('/uploads') ? p.file_path : `/uploads/${p.file_path}`) : ''}
                            alt={p.caption || 'line item photo'} loading="lazy"
                            onError={e => { e.target.style.display = 'none'; }} />
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="pest-items__td pest-items__td--num fcp-tabular" data-label="Qty">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</td>
                  <td className="pest-items__td pest-items__td--num" data-label="Unit price"><Money amount={item.unit_price} /></td>
                  <td className="pest-items__td pest-items__td--num" data-label="Amount">
                    {/* The two figures are wrapped so they stay ONE thing
                        however the cell is laid out. Below the card
                        breakpoint the cell is a flex row, and two loose
                        children are two flex ITEMS: measured 2026-09-13 at
                        320px they rendered side by side on the same line,
                        reading as two amounts rather than a price and its
                        discount. No figure changed. */}
                    <span className="pest-items__amount">
                      {disc > 0 && <span className="pest-items__wasprice"><Money amount={gross} /></span>}
                      <Money amount={net} />
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {/* ── Totals ───────────────────────────────────────────────── */}
        <section className="pest-totals">
          <div className="pest-totals__rows">
            <div className="pest-totals__row">
              <span>Subtotal</span>
              <Money amount={estimate.subtotal} />
            </div>
            {/* `estimate.discount_amount` is the RAW input: dollars when
                discount_type is 'fixed', a PERCENT when it is 'percent'
                (routes/estimates.js — "Persist the RAW discount input the user
                typed (50 = $50 if fixed, 50% if percent)"). Rendering it
                straight through <Money> quoted a 10%-off $2,000 job as
                "Discount −$10.00" above a total of $1,800. Resolved exactly as
                the per-line row below already does. */}
            {(() => {
              const estDisc = resolveDiscount(
                estimate.subtotal, estimate.discount_amount, estimate.discount_type,
              );
              const isPct = estimate.discount_type === 'percent'
                && parseFloat(estimate.discount_amount) > 0;
              return estDisc > 0 ? (
                <div className="pest-totals__row pest-totals__row--positive">
                  <span>
                    Discount
                    {isPct && (
                      <span className="pest-totals__row-desc fcp-tabular"> ({parseFloat(estimate.discount_amount)}%)</span>
                    )}
                    {estimate.discount_description && (
                      <span className="pest-totals__row-desc"> ({estimate.discount_description})</span>
                    )}
                  </span>
                  <span>−<Money amount={estDisc} /></span>
                </div>
              ) : null;
            })()}
            {(() => {
              // computeLineItems, the SERVER's own function — the one that
              // built the stored total. Not a third hand-rolled sum.
              //
              // This row was left on the raw gross when the Discount COLUMN
              // above it was settled to cents, so one figure had two rules
              // with the column printed directly over the row. MEASURED across
              // 115,472 (quantity, price, percent) combinations: 5,766
              // disagreed — 0.5 x $0.01 at 50% gives $0.01 in the column and
              // $0.00 in the row, which does not even render. Going through
              // the server's function is what stops the two being edited apart
              // again.
              const { lineDiscountTotal: lineDisc } = computeLineItems(items || []);
              return lineDisc > 0 ? (
                <div className="pest-totals__row pest-totals__row--positive">
                  <span>Line-item discounts</span>
                  <span>−<Money amount={lineDisc} /></span>
                </div>
              ) : null;
            })()}
            {parseFloat(estimate.tax_rate) > 0 && (
              <div className="pest-totals__row">
                <span>Tax <span className="pest-totals__row-desc fcp-tabular">({estimate.tax_rate}%)</span></span>
                <Money amount={estimate.tax_amount} />
              </div>
            )}
            <div className="pest-totals__row pest-totals__row--grand">
              <span>Total</span>
              <Money amount={estimate.total} />
            </div>
          </div>
        </section>

        {/* ── Notes ─────────────────────────────────────────────────── */}
        {estimate.notes && (
          <section className="pest-notes">
            <div className="pest-notes__label">Notes</div>
            <p className="pest-notes__body">{estimate.notes}</p>
          </section>
        )}

        {/* ── Photos ────────────────────────────────────────────────── */}
        {photos && photos.length > 0 && (
          <section className="pest-photos">
            <h3 className="pest-photos__heading">Reference photos</h3>
            <div className="pest-photos__grid">
              {photos.map((photo, idx) => (
                <figure key={idx} className="pest-photo">
                  <img
                    src={photo.file_path
                      ? (photo.file_path.startsWith('/uploads')
                          ? photo.file_path
                          : `/uploads/${photo.file_path}`)
                      : ''}
                    alt={photo.caption || 'Reference photo'}
                    className="pest-photo__img"
                    onError={e => { e.target.src = ''; e.target.alt = 'Image not found'; }}
                  />
                  {(photo.caption || photo.media_caption) && (
                    <figcaption className="pest-photo__caption">
                      {photo.caption || photo.media_caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* ── Action panel ──────────────────────────────────────────── */}
        {canRespond && (
          <section className="pest-actions">
            {actionState.actionError && (
              <div className="pest-actions__error" role="alert">{actionState.actionError}</div>
            )}
            {!actionState.decliningOpen ? (
              <div className="pest-actions__btns">
                <button
                  type="button"
                  className="pest-btn pest-btn--primary"
                  onClick={handleAccept}
                  disabled={actionState.busy}
                >
                  {actionState.busy ? 'Submitting…' : 'Accept estimate'}
                </button>
                <button
                  type="button"
                  className="pest-btn pest-btn--ghost"
                  onClick={() => setActionState(s => ({ ...s, decliningOpen: true }))}
                  disabled={actionState.busy}
                >
                  Decline
                </button>
              </div>
            ) : (
              <div className="pest-decline">
                <label htmlFor="decline-reason" className="pest-decline__label">
                  Optional — let us know why
                </label>
                <textarea
                  id="decline-reason"
                  rows={3}
                  className="pest-decline__textarea"
                  placeholder="e.g. budget constraints, timing, going a different direction…"
                  value={actionState.declineReason}
                  onChange={e => setActionState(s => ({ ...s, declineReason: e.target.value }))}
                  disabled={actionState.busy}
                />
                <div className="pest-decline__btns">
                  <button
                    type="button"
                    className="pest-btn pest-btn--danger"
                    onClick={handleDecline}
                    disabled={actionState.busy}
                  >
                    {actionState.busy ? 'Submitting…' : 'Confirm decline'}
                  </button>
                  <button
                    type="button"
                    className="pest-btn pest-btn--ghost"
                    onClick={() => setActionState(s => ({ ...s, decliningOpen: false }))}
                    disabled={actionState.busy}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Just-accepted / declined banners ──────────────────────── */}
        {actionState.justAccepted && (
          <section className="pest-justdone pest-justdone--accepted">
            <h3>Thank you — estimate accepted.</h3>
            <p>{company?.name} has been notified. They'll be in touch about next steps.</p>
          </section>
        )}
        {actionState.justDeclined && (
          <section className="pest-justdone pest-justdone--declined">
            <h3>Estimate declined.</h3>
            <p>Thank you for letting us know. {company?.name} has been notified.</p>
          </section>
        )}

        {/* ── Footer ────────────────────────────────────────────────── */}
        <footer className="pest-foot">
          <div className="pest-foot__company">
            {company?.name}
            {company?.phone && <span> · {company.phone}</span>}
            {company?.email && <span> · {company.email}</span>}
          </div>
        </footer>
      </article>

      <div className="pest-actions-off no-print">
        <button onClick={() => window.print()} className="pest-print">
          Print or save as PDF
        </button>
      </div>
    </div>
  );
}

export default PublicEstimate;
